<template>
  <div class="container-fluid">
    <div class="row mb-3">
      <div class="col-12">
        <h4 class="mb-1">Tarifas</h4>
        <p class="text-muted mb-0">
          Valores usados por las apps para estimar viajes y repartir ganancias.
        </p>
      </div>
    </div>

    <div v-if="errorMessage" class="alert alert-danger">{{ errorMessage }}</div>
    <div v-if="successMessage" class="alert alert-success">{{ successMessage }}</div>

    <div class="card">
      <div class="card-body">
        <div v-if="loading" class="text-center py-5 text-muted">Cargando tarifas…</div>
        <form v-else class="theme-form" @submit.prevent="save">
          <h6 class="mb-3">Costo por kilómetro</h6>
          <div class="row">
            <div class="col-md-4">
              <div class="form-group">
                <label class="col-form-label">CostPerKM (&lt; 5 km)</label>
                <input v-model.number="form.costPerKm" type="number" min="0" step="1" class="form-control" required />
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label class="col-form-label">CostPerKM2 (5–11 km)</label>
                <input v-model.number="form.costPerKm2" type="number" min="0" step="1" class="form-control" required />
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label class="col-form-label">CostPerKM3 (≥ 11 km)</label>
                <input v-model.number="form.costPerKm3" type="number" min="0" step="1" class="form-control" required />
              </div>
            </div>
          </div>

          <h6 class="mb-3 mt-2">Recargos e impuestos</h6>
          <div class="row">
            <div class="col-md-4">
              <div class="form-group">
                <label class="col-form-label">Tarifa nocturna (%)</label>
                <input v-model.number="form.nightRatePercent" type="number" min="0" max="100" step="0.1" class="form-control" required />
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label class="col-form-label">Impuesto conductor (%)</label>
                <input v-model.number="form.driverTaxPercent" type="number" min="0" max="100" step="0.1" class="form-control" required />
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label class="col-form-label">Deuda máxima conductor</label>
                <input v-model.number="form.maxDriverDebt" type="number" min="0" step="1" class="form-control" required />
              </div>
            </div>
          </div>

          <h6 class="mb-3 mt-2">Reparto del viaje</h6>
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label class="col-form-label">% ganancia del conductor</label>
                <input
                  v-model.number="form.driverGainPercent"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  class="form-control"
                  required
                >
                <small class="form-text text-muted">
                  Porcentaje del total que recibe el conductor (bruto). La app queda con
                  {{ appCommissionPercent }}%.
                </small>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label class="col-form-label">% ahorro del conductor</label>
                <input
                  v-model.number="form.driverSavingsPercent"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  class="form-control"
                  required
                >
                <small class="form-text text-muted">
                  Porcentaje de la comisión de la app que se destina al ahorro del conductor.
                </small>
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-end mt-3">
            <button type="button" class="btn btn-light mr-2" :disabled="saving" @click="load">
              Recargar
            </button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{ saving ? 'Guardando…' : 'Guardar tarifas' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchSettings, updateSettings } from '@/services/settingsApi';

export default {
  name: 'TarifasPage',
  data() {
    return {
      loading: true,
      saving: false,
      errorMessage: '',
      successMessage: '',
      form: {
        costPerKm: 0,
        costPerKm2: 0,
        costPerKm3: 0,
        nightRatePercent: 0,
        driverTaxPercent: 0,
        driverGainPercent: 70,
        driverSavingsPercent: 20,
        maxDriverDebt: 0,
      },
    };
  },
  computed: {
    appCommissionPercent() {
      const gain = Number(this.form.driverGainPercent);
      if (!Number.isFinite(gain)) return '—';
      return Math.max(0, Math.round((100 - gain) * 10) / 10);
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const data = await fetchSettings();
        this.form = {
          costPerKm: Number(data.costPerKm ?? 0),
          costPerKm2: Number(data.costPerKm2 ?? 0),
          costPerKm3: Number(data.costPerKm3 ?? 0),
          nightRatePercent: Number(data.nightRatePercent ?? 0),
          driverTaxPercent: Number(data.driverTaxPercent ?? 0),
          driverGainPercent: Number(data.driverGainPercent ?? 70),
          driverSavingsPercent: Number(data.driverSavingsPercent ?? 20),
          maxDriverDebt: Number(data.maxDriverDebt ?? 0),
        };
      } catch (e) {
        this.errorMessage = e.message || 'Error al cargar tarifas';
      } finally {
        this.loading = false;
      }
    },
    async save() {
      this.saving = true;
      this.errorMessage = '';
      this.successMessage = '';
      try {
        await updateSettings({ ...this.form });
        this.successMessage = 'Tarifas actualizadas correctamente';
        await this.load();
      } catch (e) {
        this.errorMessage = e.message || 'Error al guardar tarifas';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
